import React, { useState } from "react";
import { Upload } from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { rfiAPI } from "../../../services/api";

const InternalRFIView = () => {
  const { activeProject, projectMembers = [] } = useProjectContext();
  const projectId = activeProject?.id;

  const [subject, setSubject] = useState("");
  const [title, setTitle] = useState("");
  const [recipientIds, setRecipientIds] = useState([]);
  const [file, setFile] = useState(null);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  if (!projectId) {
    return (
      <div className="text-sm text-gray-600">
        Select a project to send an internal RFI.
      </div>
    );
  }

  const memberIds = projectMembers
    .map((m) => m.user_id)
    .filter((id) => id != null);

  const toggleRecipient = (id) => {
    setRecipientIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const allSelected =
    memberIds.length > 0 && memberIds.every((id) => recipientIds.includes(id));

  const toggleSelectAll = () => {
    setRecipientIds((prev) => (allSelected ? [] : memberIds));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!file) {
      setError("Attachment is required.");
      return;
    }
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!subject.trim()) {
      setError("Subject is required.");
      return;
    }
    if (!recipientIds.length) {
      setError("Select at least one recipient.");
      return;
    }

    try {
      setSending(true);
      const fd = new FormData();
      fd.append("subject", subject.trim());
      fd.append("title", title.trim());
      recipientIds.forEach((id) => fd.append("to_user_ids", String(id)));
      fd.append("file", file);

      await rfiAPI.sendInternal(projectId, fd);

      setSuccess("RFI uploaded and email sent.");
      setSubject("");
      setTitle("");
      setRecipientIds([]);
      setFile(null);
    } catch (e2) {
      console.error(e2);
      const msg =
        e2?.response?.data?.detail || e2?.message || "Failed to upload RFI.";
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Upload RFI</h2>
          <p className="text-gray-600 text-sm">
            Upload an RFI document, store it in project documents, and notify
            selected project members by email.
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 text-sm">
          {success}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
            placeholder="e.g. Jet Fuel Laterals – Internal Clarification"
          />
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
            placeholder="e.g. Jet Fuel Laterals – Design Clarification"
          />
          <p className="mt-1 text-xs text-gray-500">
            This title will be used as the document name in the Documents list
            and in the email.
          </p>
        </div>

        {/* Recipients */}
        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Recipients (project members)
            </label>
            {memberIds.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
                <span>Select all</span>
              </label>
            )}
          </div>

          <div className="mt-2 border rounded-md p-2 max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2">
            {projectMembers.map((m) => {
              const id = m.user_id;
              const checked = recipientIds.includes(id);
              return (
                <label
                  key={id}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    checked={checked}
                    onChange={() => toggleRecipient(id)}
                  />
                  <span>
                    {m.first_name} {m.last_name}
                    {m.role ? ` • ${m.role}` : ""}
                    {m.email ? ` – ${m.email}` : ""}
                  </span>
                </label>
              );
            })}
          </div>

          <p className="mt-1 text-xs text-gray-500">
            Tick one or more project members to notify.
          </p>
        </div>

        {/* Attachment */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Attachment
          </label>
          <div className="mt-1 flex items-center gap-3">
            <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm cursor-pointer hover:bg-gray-50">
              <Upload className="w-4 h-4" />
              <span>{file ? file.name : "Choose file..."}</span>
              <input
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setFile(f || null);
                }}
              />
            </label>
            {file && <span className="text-xs text-gray-500">{file.type}</span>}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={sending}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-60"
        >
          <Upload className="w-4 h-4" />
          {sending ? "Uploading..." : "Upload RFI"}
        </button>
      </form>
    </div>
  );
};

export default InternalRFIView;
